import React from 'react';
import { CheckIcon } from 'lucide-react';
import { ScreenShell, Headline, Subtext } from '../components/ScreenShell';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { OTHER_INTEREST } from '../data/quizOptions';
import type { InterestKey, PersonalityKey, PriorityKey } from '../data/quizOptions';
import { useCopy } from '../contexts/LanguageContext';
import { adaptLabel } from '../utils/gender';
import type { PersonalizationProfile } from '../types/profile';
interface SummaryScreenProps {
  profile: PersonalizationProfile;
  customInterest: string;
  submitting: boolean;
  error: string;
  onConsentChange: (value: boolean) => void;
  onSubmit: () => void;
  onEdit: () => void;
}
function SummaryRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="border-b border-lavender-100 py-3.5 last:border-none" data-id="element-175" data-mp="lw5xnndkqtyz">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-176" data-mp="lu2xwbmmdmh2">{label}</p>
      <p className="mt-1 text-[15px] leading-snug text-ink" data-id="element-177" data-mp="gv8eb7gtvtvw">{value}</p>
    </div>;
}
export function SummaryScreen({
  profile,
  customInterest,
  submitting,
  error,
  onConsentChange,
  onSubmit,
  onEdit
}: SummaryScreenProps) {
  const copy = useCopy();
  const interests = profile.child_interests.map(key => key === OTHER_INTEREST && customInterest.trim() ? customInterest.trim() : copy.options.interests[key as InterestKey]).join(', ');
  const personality = profile.child_personality.map(key => adaptLabel(copy.options.personality[key as PersonalityKey], profile.child_gender)).join(', ');
  const priorities = profile.development_priorities.map(key => copy.options.priorities[key as PriorityKey].title).join(', ');
  const genderLabel = profile.child_gender === 'daughter' ? copy.steps.name.daughter : copy.steps.name.son;
  return <ScreenShell data-id="element-178" data-mp="k8cy86qzfr2r">
      <Headline data-id="element-179" data-mp="k7trlm6s8p41">{copy.summary.headline(profile.child_name)}</Headline>

      <div className="mt-6 rounded-4xl border border-lavender-200 bg-white/90 px-5 py-1 shadow-soft" data-id="element-180" data-mp="ka695y8moxgk">
        <SummaryRow label={copy.summary.genderRow} value={genderLabel} data-id="element-181" data-mp="2c9whufgf0j9g" />
        <SummaryRow label={copy.summary.ageRow} value={copy.summary.ageValue(profile.child_age)} data-id="element-182" data-mp="2c9mrurvdec3r" />
        <SummaryRow label={copy.summary.interestsRow} value={interests} data-id="element-183" data-mp="2q8w4xioa4q3i" />
        <SummaryRow label={copy.summary.personalityRow} value={personality} data-id="element-184" data-mp="2lbm5rbb3902h" />
        <SummaryRow label={copy.summary.prioritiesRow} value={priorities} data-id="element-185" data-mp="1xkgqax9289sp" />
      </div>

      <Subtext data-id="element-186" data-mp="1sm6xuuepznas">{copy.summary.note(profile.child_name)}</Subtext>

      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-3xl border border-lavender-200 bg-lavender-50 p-4" data-id="element-187" data-mp="1sju6xrqzi2ll">
        <span className="relative mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center" data-id="element-188" data-mp="22kcyz9spcpel">
          <input type="checkbox" checked={profile.parental_consent} onChange={event => onConsentChange(event.target.checked)} className="peer h-6 w-6 appearance-none rounded-lg border border-lavender-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-violet-400/25" data-id="element-189" data-mp="1euilcq0k6wta" />
          <CheckIcon aria-hidden="true" className="pointer-events-none absolute h-4 w-4 text-white opacity-0 peer-checked:opacity-100" data-id="element-190" data-mp="3ci2zdizf4ydj" />
        </span>
        <span className="text-[13px] leading-relaxed text-ink-soft" data-id="element-191" data-mp="3ghbqsh6zkk5x">{copy.summary.consent}</span>
      </label>

      {error && <p role="alert" className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600" data-id="element-192" data-mp="d3hetfk4sbne">
          {error}
        </p>}

      <div className="mt-6 space-y-1" data-id="element-193" data-mp="3v7sr1x7vx418">
        <PrimaryButton onClick={onSubmit} disabled={!profile.parental_consent} loading={submitting} data-id="element-194" data-mp="3vax5roiz7i65">
          {submitting ? copy.summary.sending : copy.summary.submit(profile.child_name)}
        </PrimaryButton>
        <PrimaryButton variant="ghost" onClick={onEdit} disabled={submitting} data-id="element-195" data-mp="2ot9xz7011ybj">
          {copy.summary.edit}
        </PrimaryButton>
      </div>
    </ScreenShell>;
}